/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * App Component - Main Application Entry Point
 *
 * Handles routing and global state initialization.
 * Supports both authenticated users and guest checkout.
 */

import * as React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
} from "react-router-dom";
import { createLocalizeRouter } from "@/utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { Loading } from "@/components/loading";
import ResetPasswordPage from "@/pages/reset-password";
import { useGetProfileQuery } from "@/redux/services/profile";
import {
  adaptActiveSubscription,
  adaptLifetimeProducts,
  adaptProducts,
  adaptSubscriptionProducts,
} from "@/adapters/productsAdapter";
import {
  setActiveSubscriptions,
  setInvoices,
  setProducts,
} from "@/redux/slices/userSlice";
import { useGetOrdersQuery } from "@/redux/services/orders";
import StripeAddress from "./components/cartpage/address";
import type { RootState } from "@/redux/store";
import { authClient } from "@/lib/auth-client";
import { logger } from "@/lib/logger";

const RootLayout = React.lazy(() => import("@/layouts/root"));
const DashboardLayout = React.lazy(() => import("@/layouts/dashboard"));
const DashboardHomePage = React.lazy(
  () => import("@/pages/dashboard/overview"),
);
const ProductsPage = React.lazy(() => import("@/pages/dashboard/products"));
const ProductDetailsPage = React.lazy(
  () => import("@/pages/dashboard/license-details"),
);

const AccountDetailsPage = React.lazy(
  () => import("@/pages/dashboard/account-settings"),
);
const BillingPaymentsPage = React.lazy(
  () => import("@/pages/dashboard/billing-payments"),
);

const CartPage = React.lazy(() => import("@/pages/cart"));
const ThankYouPage = React.lazy(() => import("@/pages/thank-you"));
const CheckoutPage = React.lazy(() => import("@/pages/checkout"));
const ReturnPage = React.lazy(() =>
  import("@/pages/checkout").then((module) => ({ default: module.Return })),
);
const Listpage = React.lazy(() => import("@/pages/list"));
const PaymentErrorPage = React.lazy(
  () => import("@/pages/payment-error"),
);

const OfflineActivationPage = React.lazy(
  () => import("@/pages/offline-activation"),
);
const TrialVerifyPage = React.lazy(() => import("@/pages/trial-verify"));
const TrialRegisterPage = React.lazy(() => import("@/pages/trial-register"));
const TrialStatusPage = React.lazy(() => import("@/pages/trial-status"));
const TrialUnsubscribedPage = React.lazy(() => import("@/pages/trial-unsubscribed"));
const UnsubscribeCartPage = React.lazy(() => import("@/pages/unsubscribe-cart"));
const MigrationUnsubscribePage = React.lazy(() => import("@/pages/migration-unsubscribe"));
// S428: Desktop WebView upgrade page (Annual→Lifetime from within the Windows app)
const UpgradeCheckoutPage = React.lazy(() => import("@/pages/upgrade-checkout"));
const ErrorPage = React.lazy(() => import("@/pages/error"));
const RouteErrorBoundary = React.lazy(() =>
  import("@/pages/error").then((module) => ({
    default: module.RouteErrorBoundary,
  })),
);
const router = createBrowserRouter(
  createLocalizeRouter([
    {
      path: "/",
      element: (
        <React.Suspense fallback={<Loading />}>
          <RootLayout />
        </React.Suspense>
      ),
      errorElement: (
        <React.Suspense fallback={<Loading />}>
          <RouteErrorBoundary />
        </React.Suspense>
      ),
      children: [
        {
          path: "/",
          element: (
            // S531: This redirect is the unauthenticated root fallback — it fires
            // before any /:lang param is established. It correctly defaults to "en"
            // (the only fully-translated locale) so the router can then pick up the
            // user's real lang once they navigate further. All post-auth navigations
            // use useLangPrefix() and are fully language-aware.
            <Navigate to="/en/dashboard/overview" replace />
          ),
        },
        {
          path: "/dashboard",
          element: (
            <React.Suspense fallback={<Loading />}>
              <DashboardLayout />
            </React.Suspense>
          ),
          children: [
            {
              path: "/dashboard/overview",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <DashboardHomePage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/products",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/subscription/:id",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductDetailsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/product",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ProductDetailsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/billing-payments",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <BillingPaymentsPage />
                </React.Suspense>
              ),
            },
            {
              path: "/dashboard/account-settings",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <AccountDetailsPage />
                </React.Suspense>
              ),
            },
          ],
        },
        {
          path: "",
          element: (
            <React.Suspense fallback={<Loading />}>
              <Outlet />
            </React.Suspense>
          ),
          children: [
            {
              path: "/checkout",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CheckoutPage />
                </React.Suspense>
              ),
            },
            {
              path: "/return",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ReturnPage />
                </React.Suspense>
              ),
            },
            {
              path: "/payment-error",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <PaymentErrorPage />
                </React.Suspense>
              ),
            },
            {
              path: "/add-to-cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CartPage />
                </React.Suspense>
              ),
            },
            {
              path: "/cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <CartPage />
                </React.Suspense>
              ),
            },
            {
              path: "/list",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <Listpage />
                </React.Suspense>
              ),
            },
            {
              path: "/thanks",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ThankYouPage />
                </React.Suspense>
              ),
            },
            {
              path: "/offline/:data",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <OfflineActivationPage />
                </React.Suspense>
              ),
            },
            {
              path: "/address",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <StripeAddress />
                </React.Suspense>
              ),
            },
            {
              path: "/reset-password",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <ResetPasswordPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/verify/:token",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialVerifyPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/register",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialRegisterPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/status",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialStatusPage />
                </React.Suspense>
              ),
            },
            {
              path: "/trial/unsubscribed",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <TrialUnsubscribedPage />
                </React.Suspense>
              ),
            },
            {
              path: "/unsubscribe-cart",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <UnsubscribeCartPage />
                </React.Suspense>
              ),
            },
            {
              // S499: One-click opt-out for migration campaign emails.
              // Token is generated per OldCustomer row and embedded in the
              // offer email. Backend: GET /api/v1/migration-campaign/unsubscribe/
              path: "/migration/unsubscribe",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <MigrationUnsubscribePage />
                </React.Suspense>
              ),
            },
            {
              // S428: Desktop WebView page for Annual→Lifetime upgrade initiated
              // from within the Windows application. The URL carries an encrypted
              // payload (?p=...) that the page decrypts to recover the Annual
              // activation key, email, machine ID, and priceId — no manual input.
              path: "/upgrade/checkout",
              element: (
                <React.Suspense fallback={<Loading />}>
                  <UpgradeCheckoutPage />
                </React.Suspense>
              ),
            },
          ],
        },
        {
          path: "*",
          element: (
            <React.Suspense fallback={<Loading />}>
              <ErrorPage />
            </React.Suspense>
          ),
        },
      ],
    },
  ]),
);

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootState) => state.user.isAuthenticated,
  );

  // Check Better Auth session for authenticated users
  const { data: session } = authClient.useSession();
  const hasValidSession = Boolean(session?.user);

  // Only fetch profile and orders for authenticated users
  // Skip for guests to allow guest checkout without API errors
  const {
    data: stripeCustomer,
    error: profileError,
    isLoading: profileLoading,
  } = useGetProfileQuery(undefined, {
    skip: !hasValidSession && !isAuthenticated,
  });
  const {
    data: orders,
    error: ordersError,
    isLoading: ordersLoading,
  } = useGetOrdersQuery(undefined, {
    skip: !hasValidSession && !isAuthenticated,
  });

  React.useEffect(() => {
    // Log API errors for debugging (only when authenticated)
    if (profileError && (hasValidSession || isAuthenticated)) {
      logger.error("Profile API error:", profileError);
    }
    if (ordersError && (hasValidSession || isAuthenticated)) {
      logger.error("Orders API error:", ordersError);
    }
  }, [profileError, ordersError, hasValidSession, isAuthenticated]);

  React.useEffect(() => {
    const fetchSubscriptions = async () => {
      // Don't proceed if data is loading
      if (profileLoading || ordersLoading) {
        logger.debug("Still loading profile or orders...");
        return;
      }

      // Check if we have the required data
      if (!stripeCustomer) {
        logger.debug("No stripe customer data available");
        return;
      }

      if (!orders) {
        logger.debug("No orders data available");
        return;
      }

      logger.debug("Fetching subscriptions with:", {
        hasStripeCustomer: !!stripeCustomer,
        hasOrders: !!orders,
        ordersCount: Array.isArray(orders) ? orders.length : 0,
      });

      try {
        dispatch(setInvoices(orders));

        // Adapt active subscriptions with error handling
        let activeSubscriptions: Awaited<
          ReturnType<typeof adaptActiveSubscription>
        > = [];
        try {
          activeSubscriptions = await adaptActiveSubscription(stripeCustomer, orders ?? []);
          logger.debug("Active subscriptions:", activeSubscriptions.length);
        } catch (error) {
          logger.error("Error adapting active subscriptions:", error);
        }
        dispatch(setActiveSubscriptions(activeSubscriptions));

        // Adapt subscription products with error handling
        let subscriptionProductsData: Awaited<
          ReturnType<typeof adaptSubscriptionProducts>
        > = [];
        try {
          subscriptionProductsData =
            await adaptSubscriptionProducts(activeSubscriptions, orders ?? []);
          logger.debug(
            "Subscription products:",
            subscriptionProductsData.length,
          );
        } catch (error) {
          logger.error("Error adapting subscription products:", error);
        }

        // Adapt lifetime products with error handling
        let lifetimeProducts: Awaited<
          ReturnType<typeof adaptLifetimeProducts>
        > = [];
        try {
          lifetimeProducts = await adaptLifetimeProducts(orders);
          logger.debug("Lifetime products:", lifetimeProducts.length);
        } catch (error) {
          logger.error("Error adapting lifetime products:", error);
          // lifetimeProducts stays empty array
        }

        // Combine products with error handling
        try {
          const products = await adaptProducts(
            subscriptionProductsData,
            lifetimeProducts,
          );
          logger.debug("Total products dispatched:", products.length);
          dispatch(setProducts(products));
        } catch (error) {
          logger.error("Error adapting products:", error);
          // Dispatch empty products to prevent stuck loading state
          dispatch(setProducts([]));
        }
      } catch (error) {
        logger.error("Error in fetchSubscriptions:", error);
      }
    };
    fetchSubscriptions();
  }, [stripeCustomer, orders, dispatch, profileLoading, ordersLoading]);

  return (
    <React.Suspense fallback={<Loading />}>
      <RouterProvider router={router} />
    </React.Suspense>
  );
}

export default App;
